import { Point } from '../../../core/models/point';
import { Figure } from './figure';
import * as jsts from 'jsts';
import { konvaSettings } from '../konvaSettings';
import { Utils } from '../../../core/services/utils.service';
import { Equipment } from '../equipment';
import { Bounding } from '../../../core/models/bounding';
import { Range } from '../../../core/models/range';
import { UnitConverter } from '../../../core/utils/unitConverter';
import { applyShapeFillParamsFromLoad, setShapeFillColor } from '../elementFillColor';

const Coordinate = jsts.geom.Coordinate,
	GeometryFactory = jsts.geom.GeometryFactory,
	geometryFactory: jsts.geom.GeometryFactory = new GeometryFactory();

export class RectangleSliced implements Figure {
	type: string = 'rectangle-sliced';
	begin: Point;
	width: number;
	depth: number;
	frontHeight: number;
	backHeight: number;
	representation: string = undefined;
	priority: 'normal';
	wallWidth: number; // boxes
	frontAngle: number = 0;
	rotationPoint: { x: number, y: number };
	strokeColor: string = konvaSettings.fillDarkColor;
	color?: string;
	fillColor: string = konvaSettings.fillBoxColor;
	empty: {
		front: boolean,   // відмальовка порожнистого обєкта
		top: boolean
	};
	prevObjectHash: string;

	cache: any = RectangleSliced.returnClearCacheObject();

	get square(): number {
		return Utils.round(this.width * this.depth);
	}

	constructor(params: any) {
		this.wallWidth = UnitConverter.convertAndRoundToSaveValue(0.01);

		const {
				empty,
				begin,
				frontHeight,
				backHeight,
				width,
				depth,
				fillColor,
				priority,
				rotationPoint,
				frontAngle,
				wallWidth,
				representation
			} = params,
			validatedParams: any = this.validate({
				empty,
				begin,
				frontHeight,
				backHeight,
				width,
				depth,
				fillColor,
				priority,
				rotationPoint,
				wallWidth,
				frontAngle,
				representation
			});
		Object.assign(this, validatedParams);
		applyShapeFillParamsFromLoad(this, params);

		if (!rotationPoint) {
			this.rotationPoint = { x: this.begin.z, y: this.begin.y };
		}
	}

	set beginSetter(value: Point) {
		this.begin = {
			x: Utils.round(value.x),
			y: Utils.round(value.y),
			z: Utils.round(value.z),
		};

		this.cache = RectangleSliced.returnClearCacheObject();
	}

	get coordinates(): Array<Point> {
		const coordinates = this.getCoordinates().bottom;

		return [...coordinates, coordinates[0]];
	}

	get savedParams() {
		const {
			empty,
			begin,
			frontHeight,
			backHeight,
			width,
			depth,
			color,
			fillColor,
			priority,
			rotationPoint,
			frontAngle,
			wallWidth,
			representation,
			type
		} = this;
		return {
			empty,
			begin,
			frontHeight: frontHeight ?? 0,
			backHeight: backHeight ?? 0,
			width: width ?? 0,
			depth: depth ?? 0,
			color,
			fillColor,
			priority,
			rotationPoint,
			frontAngle,
			wallWidth: wallWidth ?? UnitConverter.convertAndRoundToSaveValue(0.01),
			representation,
			type
		};
	}

	static returnClearCacheObject() {
		return {
			front: {},
			top: {},
			side: {}
		};
	}

	private getCachedValue(params) {
		const { calculate_func, key, representation = this.representation } = params;

		if (!this.cache[representation]) {
			this.cache[representation] = {};
		}

		if (this.prevObjectHash) {
			const { begin } = JSON.parse(this.prevObjectHash);

			if (!Utils.equalObjects(begin, this.begin)) {
				delete this.cache[representation][key];
			}
		}

		if (!this.cache[representation][key]) {
			if (typeof calculate_func == 'function') {
				this.cache[representation][key] = calculate_func();
			}
		}

		return this.cache[representation][key];
	}

	isEqual(hash: string): boolean {
		return this.prevObjectHash == hash;
	}

	update(params: any): void {
		Object.keys(params).forEach((param: string) => {
			if (this[param] != undefined && params[param] != null) this[param] = params[param];
		});

		const storedFill = params.color ?? params.fillColor;

		if (storedFill) {
			setShapeFillColor(this, storedFill);
		}

		this.cache = RectangleSliced.returnClearCacheObject();
	}

	copy(): RectangleSliced {
		const {
			empty,
			begin,
			frontHeight,
			backHeight,
			width,
			depth,
			color,
			fillColor,
			priority,
			rotationPoint,
			frontAngle,
			wallWidth,
			representation
		} = this;

		return new RectangleSliced({
			frontHeight,
			backHeight,
			width,
			depth,
			color,
			fillColor,
			priority,
			frontAngle,
			wallWidth,
			representation,
			empty: Object.assign({}, empty),
			rotationPoint: Object.assign({}, rotationPoint),
			begin: Object.assign({}, begin)
		});
	}

	getBoundingX(usable_area: boolean = false): Bounding {
		return this.getCachedValue({
			key: 'getBoundingX',
			calculate_func: () => {
				const coordinates = this.getCoordinates(),
					xList = [
						...coordinates.bottom.map(p => p.x),
						...coordinates.top.map(p => p.x)
					];

				return {
					min: Utils.round(Math.min(...xList)),
					max: Utils.round(Math.max(...xList))
				};
			}
		});
	}

	getBoundingY(): Bounding {
		return this.getCachedValue({
			key: 'getBoundingY',
			calculate_func: () => {
				const coordinates = this.getCoordinates(),
					yList = [
						...coordinates.bottom.map(p => p.y),
						...coordinates.top.map(p => p.y)
					];

				return {
					min: Utils.round(Math.min(...yList)),
					max: Utils.round(Math.max(...yList))
				};
			}
		});
	}

	getBoundingZ(): Bounding {
		return this.getCachedValue({
			key: 'getBoundingZ',
			calculate_func: () => {
				const coordinates = this.getCoordinates(),
					zList = [
						...coordinates.bottom.map(p => p.z),
						...coordinates.top.map(p => p.z)
					];

				return {
					min: Utils.round(Math.min(...zList)),
					max: Utils.round(Math.max(...zList))
				};
			}
		});
	}

	getAbsoluteBoundingZ(): Bounding {
		const bottom: Array<Point> = [
				{ x: this.begin.x, y: this.begin.y, z: this.begin.z },
				{ x: this.begin.x + this.width, y: this.begin.y, z: this.begin.z },
				{ x: this.begin.x + this.width, y: this.begin.y, z: this.begin.z + this.depth },
				{ x: this.begin.x, y: this.begin.y, z: this.begin.z + this.depth }
			],
			top: Array<Point> = bottom.map((p: Point, idx: number) => {
				return { x: p.x, y: p.y + (idx < 2 ? this.backHeight : this.frontHeight), z: p.z };
			});

		const coordinates = { bottom, top },
			zList = [...coordinates.bottom.map(p => p.z), ...coordinates.top.map(p => p.z)];

		return {
			min: Utils.round(Math.min(...zList)),
			max: Utils.round(Math.max(...zList))
		};
	}

	getBoundingZBetween(compareSize: Bounding): Bounding {
		const bottom = this.getCoordinates().bottom.map(p => {
			return { x: Utils.round(p.x), z: Utils.round(p.z) };
		});

		const sizesPolygon = geometryFactory.createPolygon([
			new Coordinate(compareSize.min, 0),
			new Coordinate(compareSize.max, 0),
			new Coordinate(compareSize.max, 200),
			new Coordinate(compareSize.min, 200),
			new Coordinate(compareSize.min, 0)
		]);

		const shapePolygon = geometryFactory.createPolygon([
			new Coordinate(bottom[0].x, bottom[0].z),
			new Coordinate(bottom[1].x, bottom[1].z),
			new Coordinate(bottom[2].x, bottom[2].z),
			new Coordinate(bottom[3].x, bottom[3].z),
			new Coordinate(bottom[0].x, bottom[0].z)
		]);
		if (!sizesPolygon.intersects(shapePolygon)) {
			return {
				min: 0,
				max: 0
			};
		}
		const intersection = sizesPolygon.intersection(shapePolygon);
		const coordinates = intersection.getCoordinates();

		const zList = [...coordinates.map(p => p.y)];

		return {
			min: Utils.round(Math.min(...zList)),
			max: Utils.round(Math.max(...zList))
		};
	}

	isPointOn(x?: number, y?: number, z?: number) {
		if (x != null) {
			const xBounding = this.getBoundingX();
			x = Utils.round(x);
			if (x < xBounding.min || x > xBounding.max) return false;
		}
		if (y != null) {
			const yBounding = this.getBoundingY();
			y = Utils.round(y);
			if (y < yBounding.min || y > yBounding.max) return false;
		}
		if (z != null) {
			const zBounding = this.getAbsoluteBoundingZ();
			z = Utils.round(z);
			if (z < zBounding.min || z > zBounding.max) return false;
		}
		return true;
	}

	getObjectHash(kwargs: any = {}) {
		const {
			frontHeight,
			backHeight,
			begin,
			depth,
			frontAngle,
			rotationPoint,
			strokeColor,
			color,
			fillColor,
			type,
			wallWidth,
			width,
			representation
		} = this;

		return JSON.stringify({
			frontHeight,
			backHeight,
			begin,
			depth,
			frontAngle,
			rotationPoint,
			strokeColor,
			color,
			fillColor,
			type,
			wallWidth,
			width,
			representation,
			...kwargs
		});
	}

	drag(x?: number, y?: number, z?: number): void {
		if (x) this.begin.x = Utils.round(this.begin.x + x);
		if (y) this.begin.y = Utils.round(this.begin.y + y);
		if (z) this.begin.z = Utils.round(this.begin.z + z);

		this.cache = RectangleSliced.returnClearCacheObject();
	}

	getCoordinates() {
		let bottom: Array<Point> = [
				{ x: this.begin.x, y: this.begin.y, z: this.begin.z },
				{ x: Utils.round(this.begin.x + this.width), y: this.begin.y, z: this.begin.z },
				{
					x: Utils.round(this.begin.x + this.width),
					y: this.begin.y,
					z: Utils.round(this.begin.z + this.depth)
				},
				{ x: this.begin.x, y: this.begin.y, z: Utils.round(this.begin.z + this.depth) }
			],
			top: Array<Point> = bottom.map((p, idx) => {
				const { x, y, z } = p;

				return { x, y: Utils.round(idx < 2 ? y + this.backHeight : y + this.frontHeight), z };
			});

		if (this.frontAngle != 0 && this.representation !== 'top') {
			bottom = bottom.map(p => this.rotatePointByFrontAngle(p));
			top = top.map(p => this.rotatePointByFrontAngle(p));
		}

		return { bottom, top };
	}

	getLines(representation: string, instrument: Equipment): Array<Array<number>> {
		return this.getCachedValue({
			key: 'getLines',
			calculate_func: () => {
				switch (representation) {
					case 'front': {
						const h = instrument.Height,
							xBoundings = this.getBoundingX(),
							yBoundings = this.getBoundingY();

						return [
							[xBoundings.min, h - yBoundings.min - this.backHeight, xBoundings.max, h - yBoundings.min - this.backHeight],
							[xBoundings.max, h - yBoundings.min - this.backHeight, xBoundings.max, h - yBoundings.min],
							[xBoundings.max, h - yBoundings.min, xBoundings.min, h - yBoundings.min],
							[xBoundings.min, h - yBoundings.min, xBoundings.min, h - yBoundings.min - this.backHeight],
							[xBoundings.min, h - yBoundings.min - this.frontHeight, xBoundings.max, h - yBoundings.min - this.frontHeight],
							[xBoundings.max, h - yBoundings.min - this.frontHeight, xBoundings.max, h - yBoundings.min],
							[xBoundings.max, h - yBoundings.min, xBoundings.min, h - yBoundings.min],
							[xBoundings.min, h - yBoundings.min, xBoundings.min, h - yBoundings.min - this.frontHeight]
						];
					}
					case 'top': {
						const coordinates = this.getCoordinates().top;

						return [
							[coordinates[0].x, coordinates[0].z, coordinates[1].x, coordinates[1].z],
							[coordinates[1].x, coordinates[1].z, coordinates[2].x, coordinates[2].z],
							[coordinates[2].x, coordinates[2].z, coordinates[3].x, coordinates[3].z],
							[coordinates[3].x, coordinates[3].z, coordinates[0].x, coordinates[0].z]
						];
					}
					case 'side': {
						const h = instrument.Height,
							zBoundings = this.getBoundingZ(),
							yBoundings = this.getBoundingY();

						return [
							[zBoundings.min, h - yBoundings.max, zBoundings.max, h - yBoundings.max],
							[zBoundings.max, h - yBoundings.max, zBoundings.max, h - yBoundings.min],
							[zBoundings.max, h - yBoundings.min, zBoundings.min, h - yBoundings.min],
							[zBoundings.min, h - yBoundings.min, zBoundings.min, h - yBoundings.max]
						];
					}
				}
			},
			representation
		});
	}

	rotatePointByFrontAngle(p: Point): Point {
		let rotationX = this.rotationPoint.x,
			rotationY = this.rotationPoint.y,
			angle = -this.frontAngle * Math.PI / 180;

		return {
			x: p.x,
			z: Utils.round((p.z - rotationX) * Math.cos(angle) - (p.y - rotationY) * Math.sin(angle) + rotationX),
			y: Utils.round((p.z - rotationX) * Math.sin(angle) + (p.y - rotationY) * Math.cos(angle) + rotationY)
		};
	}

	getFrontLine(instrument: Equipment) {
		return this.getCachedValue({
			key: 'getFrontLine',
			calculate_func: () => {
				const h = instrument.Height,
					xBoundings = this.getBoundingX(),
					yBoundings = this.getBoundingY();

				return [
					xBoundings.min, h - yBoundings.min - this.frontHeight,
					xBoundings.max, h - yBoundings.min - this.frontHeight,
					xBoundings.max, h - yBoundings.min,
					xBoundings.min, h - yBoundings.min
				];
			}
		});
	}

	inRange(range: Range): boolean {
		const xBounding: Bounding = this.getBoundingX();

		return !(range.to <= xBounding.min || range.from >= xBounding.max);
	}

	private validate(params: any) {
		const validatedParams = {};
		for (let key in params) {
			if (params[key] != null) validatedParams[key] = params[key];
		}
		return validatedParams;
	}
}